import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { DualDateSlider } from "./date-slider-core";
import { formatValue } from "./formatting";
import {
  appliedLabels,
  binInRange,
  buildNumericDomain,
  clean,
  decodeRange,
  encodeRange,
  formatRangeBound,
  handleIndices,
  histogramEdges,
  inputScale,
  logScaleUnusable,
  rangeFromDrag,
  type FetchRangeHistogram,
  type RangeBounds,
  type RangeHistogram,
} from "./number-range";
import { PortalPopover } from "./PortalPopover";

/**
 * Numeric min-max filter: the ``filter_type: "range"`` widget.
 *
 * In the bar it is a trigger exactly as tall as a select ("≥ 30%", or
 * "All"), so a row of range filters never grows the filter header and
 * nothing overlaps. Clicking it opens a floating panel (portalled, so the
 * cajón never clips it) with, top to bottom:
 *
 *   - a histogram of the column over the rows of the filter's
 *     ``histogram_table``, with every OTHER filter applied; bars whose
 *     centre is inside the applied range are drawn strong, the rest faint
 *     (lightness, not hue: readable for red-green colour blindness);
 *   - the dual slider (``DualDateSlider``) over the column's global
 *     ``[min, max]``, linear or log (``slider_scale``);
 *   - two exact-value inputs, typed in display units (30 for 30 %);
 *   - "N of M left", where N is the table's own count with the current
 *     value, so it always equals the table total.
 *
 * Value contract (see ``number-range.ts``): JSON ``{"min","max"}`` in
 * the column's units, an open side omitted, ``""`` when both are open.
 *
 * Commit timing, one request per gesture: the slider commits on release
 * (a handle released on its end leaves that side open; the handle that
 * did not move keeps its applied, possibly off-grid, value); the inputs
 * commit on blur or Enter, sorting a pair typed backwards (the engine
 * answers 422 to an inverted range).
 *
 * Without bounds the track is replaced by a note and only the inputs
 * remain; without ``fetchHistogram`` (no ``histogram_table``) there are
 * no bars and no count. Never an invented domain or number.
 */
export interface NumberRangeSliderProps {
  value: string;
  onChange: (value: string) => void;
  /** ``[lo, hi]`` numeric strings from filter options; empty → no track. */
  bounds?: readonly string[];
  /** ``value_format`` of the filter: number | percent | currency. */
  format?: string | null;
  /** ``slider_scale``: linear (default) | log. */
  scale?: string | null;
  /** Filter label, repeated as the panel title. */
  label: string;
  /** Classes of the select trigger, so the control matches its row. */
  triggerClassName: string;
  /** Histogram fetcher for this filter, or undefined for no bars. */
  fetchHistogram?: (edges: number[], signal: AbortSignal) => Promise<RangeHistogram>;
  disabled?: boolean;
  embedded?: boolean;
}

export type { FetchRangeHistogram };

const BINS = 24;

function toInput(v: number | null, factor: number): string {
  return v === null ? "" : String(clean(v * factor));
}

/** Trigger text: "All", "≥ 30%", "≤ $2M" or "30% – 70%". */
export function rangeSummary(
  bounds: RangeBounds | null,
  format: string | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (!bounds || (bounds.min == null && bounds.max == null)) return t("filterbar.all");
  if (bounds.min != null && bounds.max != null) {
    return `${formatRangeBound(bounds.min, format)} – ${formatRangeBound(bounds.max, format)}`;
  }
  if (bounds.min != null) return t("filterbar.gte", { v: formatRangeBound(bounds.min, format) });
  return t("filterbar.lte", { v: formatRangeBound(bounds.max!, format) });
}

export function NumberRangeSlider({
  value,
  onChange,
  bounds,
  format,
  scale,
  label,
  triggerClassName,
  fetchHistogram,
  disabled,
  embedded,
}: NumberRangeSliderProps): JSX.Element {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const decoded = useMemo(() => decodeRange(value), [value]);
  const active = decoded != null && (decoded.min != null || decoded.max != null);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={triggerClassName}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span
          className={[
            "min-w-0 truncate",
            active ? "font-semibold" : embedded ? "text-white/75" : "text-neutral-500",
          ].join(" ")}
        >
          {rangeSummary(decoded, format, t)}
        </span>
        <ChevronDown
          size={12}
          className={embedded ? "shrink-0 text-white/60" : "shrink-0 text-neutral-400"}
        />
      </button>
      <PortalPopover
        anchorRef={anchorRef}
        open={open}
        onClose={() => setOpen(false)}
        matchAnchorWidth={false}
        role="dialog"
        ariaLabel={label}
        className={[
          "w-[18rem] rounded-lg shadow-xl",
          embedded
            ? "border border-white/15 bg-navy text-white"
            : "border border-neutral-200 bg-white text-neutral-700",
        ].join(" ")}
      >
        {open && (
          <RangePanel
            value={value}
            decoded={decoded}
            onChange={onChange}
            bounds={bounds}
            format={format}
            scale={scale}
            label={label}
            fetchHistogram={fetchHistogram}
            embedded={Boolean(embedded)}
          />
        )}
      </PortalPopover>
    </>
  );
}

interface RangePanelProps {
  value: string;
  decoded: RangeBounds | null;
  onChange: (value: string) => void;
  bounds?: readonly string[];
  format?: string | null;
  scale?: string | null;
  label: string;
  fetchHistogram?: (edges: number[], signal: AbortSignal) => Promise<RangeHistogram>;
  embedded: boolean;
}

function RangePanel({
  value,
  decoded,
  onChange,
  bounds,
  format,
  scale,
  label,
  fetchHistogram,
  embedded,
}: RangePanelProps): JSX.Element {
  const { t } = useTranslation();
  const domain = useMemo(() => buildNumericDomain(bounds, scale), [bounds, scale]);
  const labels = useMemo(() => domain.map((v) => formatRangeBound(v, format)), [domain, format]);
  const factor = inputScale(format);
  const lo = domain[0];
  const hi = domain[domain.length - 1];
  const muted = embedded ? "text-white/70" : "text-neutral-500";

  // ── Histogram: fetched while the panel is open ─────────────
  const edges = useMemo(
    () => (lo != null && hi != null && hi > lo ? histogramEdges(lo, hi, scale, BINS) : null),
    [lo, hi, scale],
  );
  const [hist, setHist] = useState<RangeHistogram | null>(null);
  const [histError, setHistError] = useState(false);
  useEffect(() => {
    if (!fetchHistogram || !edges) return undefined;
    const ctl = new AbortController();
    setHistError(false);
    fetchHistogram(edges, ctl.signal)
      .then((h) => {
        if (!ctl.signal.aborted) setHist(h);
      })
      .catch((err: unknown) => {
        if (ctl.signal.aborted) return;
        // Visible, never swallowed: the panel says the bars are missing.
        console.error("[range histogram]", err);
        setHist(null);
        setHistError(true);
      });
    return () => ctl.abort();
    // ``value`` re-runs it: "N left" depends on the applied range.
  }, [fetchHistogram, edges, value]);

  // ── Slider: local drag state, committed on release ──────
  const derived = useMemo(() => handleIndices(domain, decoded), [domain, decoded]);
  const [pending, setPending] = useState<[number, number] | null>(null);
  useEffect(() => {
    setPending(null);
  }, [value, domain]);
  const [hLo, hHi] = pending ?? derived;
  const commitSlider = (): void => {
    if (pending === null) return;
    const next = rangeFromDrag(domain, pending, derived, decoded);
    setPending(null);
    if (next !== value) onChange(next);
  };

  // ── Inputs: drafts in display units, committed on blur / Enter ──
  const [draftMin, setDraftMin] = useState(() => toInput(decoded?.min ?? null, factor));
  const [draftMax, setDraftMax] = useState(() => toInput(decoded?.max ?? null, factor));
  useEffect(() => {
    setDraftMin(toInput(decoded?.min ?? null, factor));
    setDraftMax(toInput(decoded?.max ?? null, factor));
  }, [decoded, factor]);
  const commitInputs = (): void => {
    const parse = (s: string): number | null | undefined => {
      if (s.trim() === "") return null;
      const n = Number(s);
      return Number.isFinite(n) ? clean(n / factor) : undefined;
    };
    const nMin = parse(draftMin);
    const nMax = parse(draftMax);
    if (nMin === undefined || nMax === undefined) {
      // Unreadable text: restore what is applied, emit nothing.
      setDraftMin(toInput(decoded?.min ?? null, factor));
      setDraftMax(toInput(decoded?.max ?? null, factor));
      return;
    }
    const next = encodeRange(nMin, nMax);
    const shown = decodeRange(next);
    setDraftMin(toInput(shown?.min ?? null, factor));
    setDraftMax(toInput(shown?.max ?? null, factor));
    if (next !== value) onChange(next);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") commitInputs();
  };

  const unit = format === "percent" ? "%" : format === "currency" ? "$" : "";
  const inputCls = [
    "h-7 w-full min-w-0 rounded-md border px-2 text-xs tabular-nums",
    embedded
      ? "border-white/15 bg-white/10 text-white placeholder:text-white/60"
      : "border-neutral-200 bg-white text-neutral-800 placeholder:text-neutral-400",
  ].join(" ");
  const bars = hist ? Math.max(...hist.bins, 1) : 1;

  return (
    <div className="space-y-2 p-3" data-number-range-panel>
      <div className="flex items-baseline justify-between gap-2 text-[11px]">
        <span className="truncate font-semibold">{label}</span>
        {lo != null && hi != null && (
          <span className={`shrink-0 tabular-nums ${muted}`}>
            {formatRangeBound(lo, format)} – {formatRangeBound(hi, format)}
            {scale === "log" ? ` · ${t("numrange.logScale")}` : ""}
          </span>
        )}
      </div>

      {fetchHistogram && edges && (
        <div className="h-10" aria-hidden="true">
          {hist ? (
            <svg width="100%" height="40" viewBox={`0 0 ${BINS * 10} 40`} preserveAspectRatio="none">
              {hist.bins.map((n, i) => {
                const h = (n / bars) * 38;
                const inside = binInRange(i, BINS, lo!, hi!, scale, decoded);
                return (
                  <rect
                    key={i}
                    x={i * 10 + 1}
                    y={40 - h}
                    width={8}
                    height={h}
                    className={
                      inside
                        ? embedded ? "fill-white/80" : "fill-primary"
                        : embedded ? "fill-white/20" : "fill-neutral-200"
                    }
                  />
                );
              })}
            </svg>
          ) : (
            <div
              className={`flex h-full items-center text-[11px] ${
                histError ? (embedded ? "text-white" : "text-danger-600") : muted
              }`}
            >
              {histError ? t("numrange.histogramError") : "…"}
            </div>
          )}
        </div>
      )}

      {logScaleUnusable(bounds, scale) ? (
        <p className={`text-[11px] ${muted}`}>{t("numrange.logUnusable")}</p>
      ) : domain.length > 0 ? (
        <DualDateSlider
          domain={pending ? labels : appliedLabels(labels, derived, decoded, format)}
          lo={hLo}
          hi={hHi}
          labels="selection"
          embedded={embedded}
          onChange={(nextLo, nextHi) => setPending([nextLo, nextHi])}
          onCommit={commitSlider}
        />
      ) : (
        <p className={`text-[11px] ${muted}`}>{t("numrange.noData")}</p>
      )}

      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-1.5">
        <input
          type="number"
          inputMode="decimal"
          value={draftMin}
          aria-label={t("numrange.min")}
          placeholder={`${t("numrange.min")}${unit ? ` ${unit}` : ""}`}
          onChange={(e) => setDraftMin(e.target.value)}
          onBlur={commitInputs}
          onKeyDown={onKey}
          className={inputCls}
        />
        <span className={`text-xs ${muted}`}>–</span>
        <input
          type="number"
          inputMode="decimal"
          value={draftMax}
          aria-label={t("numrange.max")}
          placeholder={`${t("numrange.max")}${unit ? ` ${unit}` : ""}`}
          onChange={(e) => setDraftMax(e.target.value)}
          onBlur={commitInputs}
          onKeyDown={onKey}
          className={inputCls}
        />
      </div>

      <div className="flex items-center justify-between gap-2 text-[11px]">
        <span className={muted} aria-live="polite">
          {hist
            ? t("numrange.remaining", {
                n: formatValue(hist.in_range, "number"),
                total: formatValue(hist.total, "number"),
              })
            : ""}
        </span>
        {decoded && (decoded.min != null || decoded.max != null) && (
          <button
            type="button"
            onClick={() => onChange("")}
            className={`rounded px-1.5 py-0.5 font-medium underline-offset-2 hover:underline ${
              embedded ? "text-white" : "text-primary"
            }`}
          >
            {t("numrange.clear")}
          </button>
        )}
      </div>
    </div>
  );
}
